import { type Mesh } from '@/gfx/mesh';

export const createConeMesh = (radius: number, height: number, segments = 6): Mesh => {
  if (segments < 3) throw new Error('cone requires at least 3 segments');
  // Each side face is one triangle with 3 unique vertices (flat shading).
  const vertCount = segments * 3;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const uvs = new Float32Array(vertCount * 2);
  const indices = new Uint16Array(segments * 3);

  for (let s = 0; s < segments; s++) {
    const a0 = (s / segments) * Math.PI * 2;
    const a1 = ((s + 1) / segments) * Math.PI * 2;
    const x0 = Math.cos(a0) * radius;
    const z0 = Math.sin(a0) * radius;
    const x1 = Math.cos(a1) * radius;
    const z1 = Math.sin(a1) * radius;

    const apex = [0, height, 0] as const;
    const bL = [x0, 0, z0] as const;
    const bR = [x1, 0, z1] as const;

    // Outward normal: (apex - mid_base) × (bR - bL), then normalize.
    const mx = (x0 + x1) * 0.5;
    const mz = (z0 + z1) * 0.5;
    const ex = -mx; // apex.x - mx = 0 - mx
    const ey = height;
    const ez = -mz;
    const ux = x1 - x0;
    const uy = 0;
    const uz = z1 - z0;
    // n = e × u  (apex-to-mid crossed with base edge); needs to point outward
    let nx = ey * uz - ez * uy;
    let ny = ez * ux - ex * uz;
    let nz = ex * uy - ey * ux;
    const inv = 1 / Math.hypot(nx, ny, nz);
    nx *= inv;
    ny *= inv;
    nz *= inv;

    const base = s * 3;
    for (let k = 0; k < 3; k++) {
      const p = k === 0 ? apex : k === 1 ? bL : bR;
      const pi = (base + k) * 3;
      positions[pi] = p[0];
      positions[pi + 1] = p[1];
      positions[pi + 2] = p[2];
      normals[pi] = nx;
      normals[pi + 1] = ny;
      normals[pi + 2] = nz;
    }
    const ui = base * 2;
    uvs[ui] = 0.5;
    uvs[ui + 1] = 0;
    uvs[ui + 2] = s / segments;
    uvs[ui + 3] = 1;
    uvs[ui + 4] = (s + 1) / segments;
    uvs[ui + 5] = 1;

    const ii = s * 3;
    // CCW from outside: apex, bR, bL (so the cross product points outward).
    indices[ii] = base;
    indices[ii + 1] = base + 2;
    indices[ii + 2] = base + 1;
  }

  return { positions, normals, uvs, indices };
};
